import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getSections, createSection } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Layers,
  Save,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// Tooltip component (reusable)
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface SectionFormData {
  section_key: string;
  parent_section_id: string;
  order: number;
  isBase: boolean;
  isTypeSpecific: boolean;
  archived: boolean;
}

interface FormErrors {
  section_key?: string;
  submit?: string;
}

export default function AddSectionPage() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const { data: sections = [] } = useQuery(getSections);
  const createSectionFn = useAction(createSection);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to add sections.</p>
        </div>
      </div>
    );
  }

  const [formData, setFormData] = useState<SectionFormData>({
    section_key: '',
    parent_section_id: '',
    order: 0,
    isBase: false,
    isTypeSpecific: false,
    archived: false
  });

  const handleChange = (field: keyof SectionFormData, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Filter out sections that could cause circular references
  const availableParentSections = (sections as any[]).filter((s: any) => s.id !== formData.parent_section_id);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.section_key.trim()) {
      newErrors.section_key = 'Section key is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await createSectionFn({
        section_key: formData.section_key,
        parent_section_id: formData.parent_section_id || undefined,
        order: formData.order,
        isBase: formData.isBase,
        isTypeSpecific: formData.isTypeSpecific,
        archived: formData.archived
      });

      toast({
        title: "Success",
        description: "Section created successfully"
      });

      navigate('/admin/sections');
    } catch (error: any) {
      console.error('Error creating section:', error);
      setErrors({ submit: error.message || 'Failed to create section. Please try again.' });
      toast({
        title: "Error",
        description: error.message || 'Failed to create section',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/admin/sections');
  };

  return (
    <div className="flex h-full">
        {/* Main Content Area */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 pl-6 pt-4">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Layers className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-content-text">Add Section</h1>
                    <p className="text-content-text-muted">Create a new section in the system</p>
                  </div>
                </div>
              </div>

              <form id="section-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* Primary Input Fields */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium text-content-text mb-4">Section Key *</h3>
                      <input
                        type="text"
                        value={formData.section_key}
                        onChange={(e) => handleChange('section_key', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                          errors.section_key ? 'border-destructive bg-destructive/10' : ''
                        }`}
                        placeholder="e.g., main_content"
                        required
                      />
                      {errors.section_key && (
                        <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.section_key}
                        </p>
                      )}
                      <p className="text-sm text-content-text-muted mt-2">Unique identifier for this section (snake_case recommended)</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-content-text mb-4">Order</h3>
                      <input
                        type="number"
                        value={formData.order}
                        onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                        placeholder="0"
                      />
                      <p className="text-sm text-content-text-muted mt-2">Display order (lower numbers appear first)</p>
                    </div>
                  </div>

                  {/* Parent Section */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Parent Section</label>
                    <select
                      value={formData.parent_section_id}
                      onChange={(e) => handleChange('parent_section_id', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border"
                    >
                      <option value="">None (Top-level section)</option>
                      {availableParentSections.map((section: any) => (
                        <option key={section.id} value={section.id}>{section.section_key}</option>
                      ))}
                    </select>
                    <p className="text-sm text-content-text-muted mt-2">Optional: Select a parent to create a nested section</p>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar - Anchored to the right */}
            <div className="w-80 border-l border-border flex flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="section-form"
                    onClick={handleSubmit}
                    disabled={isLoading || !formData.section_key.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Section Settings */}
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Section Settings</h3>

                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isBase}
                      onChange={(e) => {
                        handleChange('isBase', e.target.checked);
                        if (e.target.checked) handleChange('isTypeSpecific', false);
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Base Section</div>
                      <Tooltip content="Base sections are available to all organization types">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isTypeSpecific}
                      onChange={(e) => {
                        handleChange('isTypeSpecific', e.target.checked);
                        if (e.target.checked) handleChange('isBase', false);
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Type-Specific</div>
                      <Tooltip content="Type-specific sections are only available to certain organization types">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.archived}
                      onChange={(e) => handleChange('archived', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Archived</div>
                      <Tooltip content="Archived sections are hidden from normal views">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>
                </div>
              </div>

              {/* Help Information */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Help</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• Section key must be unique</p>
                  <p>• Use snake_case for section keys</p>
                  <p>• Base sections appear for all org types</p>
                  <p>• Type-specific sections are customizable per org type</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}



